import { useState } from 'react'
import { useAuth } from '../../auth/AuthContext'
import { club } from '../../api/club'
import { ApiError } from '../../api/client'
import { ABOUT, AboutButton } from '@versports/ui'
import { AppNavBar } from '../../components/AppNavBar'

/**
 * Gate BLOQUEANTE de T&C para STAFF de club (manager).
 * Distinto del consentimiento científico del jugador (ver HomePage.tsx,
 * `consentBlocked` — ESE gate es para el piloto, no reutilizar `Consent`
 * acá). Mismo lenguaje visual/patrón técnico: pantalla full-screen,
 * texto + botón, POST al backend, luego `refresh()` de /auth/me para que
 * `terms_ok` se actualice y el gate se cierre solo.
 *
 * Montado por `RequireStaffTerms` (auth/RequireStaffTerms.tsx), que envuelve
 * las rutas `/club` y `/club/players/:userId` — así un manager no puede
 * esquivar el gate navegando directo a una sub-ruta del panel (pedido de mg
 * 2026-07-09, "bloqueante de verdad").
 */
export function StaffTermsGate() {
  const { refresh } = useAuth()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function acceptTerms() {
    setBusy(true)
    setError(null)
    try {
      await club.recordMyTerms()
      await refresh()
    } catch (e) {
      setError(
        e instanceof ApiError ? e.message : 'No se pudo registrar la aceptación.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-b from-surface to-surface-elevated">
      <div className="flex flex-1 items-center justify-center px-lg py-2xl">
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-lg">
          <div className="rounded-md border border-primary/40 bg-surface-elevated p-lg shadow-[0_0_34px_rgba(226,90,27,0.10)]">
            <span className="tag-ok" aria-live="polite">
              Paso obligatorio antes de continuar
            </span>
            <h1 className="mt-sm">Términos y condiciones de uso</h1>
            <p className="m-0 text-sm leading-relaxed text-text-secondary">
              Antes de acceder al panel de club necesitamos que aceptes los
              términos y condiciones de uso.
            </p>
          </div>

          <div className="card flex flex-col gap-lg border-primary/30">
            {/* El párrafo que se acepta NO cambia: es el mismo que firma el
                jugador (ConsentNotice) y lo define el whitepaper en tutito.
                Lo que se agregó el 2026-09-09 es el pie con los links a los
                textos legales completos (v1.0), fuera del bloque aceptado. */}
            <div className="flex flex-col gap-md text-sm leading-relaxed text-text-secondary">
              <p className="m-0">
                VER SPORTS no reemplaza tratamiento psicológico, diagnóstico
                médico ni evaluación clínica. Es una herramienta de
                entrenamiento de habilidades mentales y rendimiento bajo
                presión.
              </p>
              <p className="m-0 text-xs text-text-muted">
                Al aceptar confirmás que leíste los{' '}
                <a href={ABOUT.termsPath} target="_blank" rel="noreferrer">
                  Términos y condiciones de uso
                </a>{' '}
                y la{' '}
                <a href={ABOUT.dataPolicyPath} target="_blank" rel="noreferrer">
                  Política de datos
                </a>
                , que incluyen tus responsabilidades como staff sobre las altas
                y los datos de tus jugadores.
              </p>
            </div>

            {error && <div className="banner-err">{error}</div>}

            <button
              type="button"
              className="btn-primary"
              disabled={busy}
              onClick={() => void acceptTerms()}
            >
              {busy
                ? 'Guardando…'
                : 'Acepto los términos y condiciones de uso de VER SPORTS'}
            </button>
          </div>
        </div>
      </div>
      <AboutButton navOffset />
      <AppNavBar />
    </div>
  )
}
